// Several to-dos chosen: what can be done with all of them at once, floating at the bottom (MacSelectionBar).

import { useRef, useState } from "react";
import { destinationOf, type MoveDestination } from "../core/actions";
import type { Start } from "../core/types";
import { t } from "../i18n";
import { useWindow } from "./context";
import { Icon } from "./Icon";
import { Popover } from "./Menu";
import { MovePicker, TagPicker, WhenPicker } from "./Pickers";

export function SelectionBar({ onComplete }: { onComplete: (ids: readonly string[]) => void }) {
  const { ui, state, model, set, showToast } = useWindow();
  const anchor = useRef<HTMLElement | null>(null);
  const [picker, setPicker] = useState<"when" | "move" | "tags" | null>(null);
  const count = ui.selected.length;
  if (count < 2 || ui.open !== null) return null;
  const todos = state.records.todos.filter((todo) => ui.selected.includes(todo.id));
  const allTags = [...new Set(state.records.todos.flatMap((todo) => todo.tags))].sort((a, b) => a.localeCompare(b));
  const commonTags = allTags.filter((tag) => todos.every((todo) => todo.tags.includes(tag)));
  // What all of them share shows as chosen; where they differ, nothing does, and Return alone changes nothing.
  const sharedStart = shared(todos.map((todo) => todo.start));
  const sharedPlace = shared(todos.map(destinationOf));
  const show = (which: "when" | "move" | "tags", element: HTMLElement) => {
    anchor.current = element;
    setPicker(which);
  };
  return (
    <div className="selection-bar" role="toolbar" aria-label={t("{count} selected", { count })}>
      <span className="count">{t("{count} selected", { count })}</span>
      <button onClick={() => { model.schedule(ui.selected, { kind: "on", day: state.today }); showToast(t("Scheduled for Today"), true); }}>
        <Icon name="today" />{t("Today")}
      </button>
      <button onClick={(event) => show("when", event.currentTarget)}><Icon name="calendar" />{t("When")}</button>
      <button onClick={(event) => show("move", event.currentTarget)}><Icon name="move" />{t("Move")}</button>
      <button onClick={(event) => show("tags", event.currentTarget)}><Icon name="tag" />{t("Tags")}</button>
      <button onClick={() => { model.delete(ui.selected); set({ selected: [] }); showToast(t("{count} to-dos deleted", { count }), true); }}>
        <Icon name="trash" />{t("Delete")}
      </button>
      <button className="complete" onClick={() => onComplete(ui.selected)}><Icon name="check" />{t("Complete")}</button>
      {picker && (
        <Popover anchor={anchor} onClose={() => setPicker(null)} label={t(picker === "when" ? "When" : picker === "move" ? "Move" : "Tags")}>
          {picker === "when" && (
            <WhenPicker start={sharedStart} today={state.today}
              onChoose={(start) => { model.schedule(ui.selected, start); setPicker(null); }} onClose={() => setPicker(null)} />
          )}
          {picker === "move" && (
            <MovePicker areas={state.records.areas} projects={state.records.projects} current={sharedPlace} progress={(project) => state.lists.progress(project)}
              onChoose={(destination) => { model.move(ui.selected, destination); setPicker(null); showToast(t("Moved"), true); }}
              onClose={() => setPicker(null)} />
          )}
          {picker === "tags" && (
            <TagPicker tags={commonTags} allTags={allTags} onToggle={(tag) => {
              const all = commonTags.includes(tag);
              model.updateMany(todos.map((todo) => ({ ...todo, tags: all ? todo.tags.filter((other) => other !== tag) : todo.tags.includes(tag) ? todo.tags : [...todo.tags, tag] })), "Tags");
            }} />
          )}
        </Popover>
      )}
    </div>
  );
}

/** The value all of them have, or undefined where they differ. */
function shared<Value extends Start | MoveDestination>(values: readonly Value[]): Value | undefined {
  const first = values[0];
  if (first === undefined) return undefined;
  const text = JSON.stringify(first);
  return values.every((value) => JSON.stringify(value) === text) ? first : undefined;
}
